import { either, taskEither } from 'fp-ts';
import { errorToReactLeft, pipe } from 'utils/fp';
import { getComputeClusterTE } from 'api/computeClusters/getComputeCluster';
import { listComputeClustersTE } from 'api/computeClusters/listComputeClusters';
import type { ComputeClusters, CreateClusterBody } from 'api/computeClusters/types';
import { postComputeClustersTE } from 'api/computeClusters/postComputeClusters';
import { patchComputeClusterTE } from 'api/computeClusters/patchComputeCluster';
import { listCloudProvidersTE } from 'api/computeClusters/listCloudProviders';
import { listCloudProviderRegionsTE } from 'api/computeClusters/listCloudProviderRegions';
import { listKeysTE } from 'api/keys';
import { displayError } from '../apiUtils';

export const clustersApiEffects = {
  listCloudProvidersTE: () =>
    pipe(
      listCloudProvidersTE(errorToReactLeft),
      taskEither.map((resp) => resp.cloud_providers),
    ),
  listCloudProviderRegionsTE: (cloudProviderId: string) =>
    pipe(
      listCloudProviderRegionsTE({ cloudProviderId }, errorToReactLeft),
      taskEither.map((resp) => resp.regions),
    ),
  fetchKeysTE: (userId: string) =>
    pipe(
      listKeysTE({ userId }, errorToReactLeft),
      taskEither.map((resp) => resp.keys),
    ),
  getComputeClusterTE: (userId: string, computeClusterId: string, sessionToken?: string) => {
    return pipe(
      getComputeClusterTE({ userId, computeClusterId, sessionToken }, errorToReactLeft),
      taskEither.map((resp) => resp.compute_cluster),
    );
  },
  listComputeClustersTE: (userId: string) =>
    pipe(
      listComputeClustersTE({ userId }, errorToReactLeft),
      taskEither.map((resp) => resp.compute_clusters),
    ),
  postComputeClustersTE: (userId: string, body: ComputeClusters) =>
    pipe(
      postComputeClustersTE({ userId, body }),
      taskEither.map((resp) => resp),
    ),
  patchComputeClusterTE: (userId: string, computeClusterId: string, body: CreateClusterBody) =>
    pipe(
      patchComputeClusterTE({ userId, computeClusterId, body }),
      taskEither.map((resp) => resp),
    ),
  createCluster: async (userId: string, computeClusters: ComputeClusters) => {
    const clusterResponse = await clustersApiEffects.postComputeClustersTE(userId, computeClusters)();

    return pipe(
      clusterResponse,
      either.fold(
        (error) => {
          displayError(error.status.details, error.status.description);
          return false;
        },
        () => {
          return true;
        },
      ),
    );
  },
  editCluster: async (userId: string, computeClusterId: string, body: CreateClusterBody) => {
    const clusterResponse = await clustersApiEffects.patchComputeClusterTE(userId, computeClusterId, body)();

    return pipe(
      clusterResponse,
      either.fold(
        (error) => {
          displayError(error.status.details, error.status.description);
          return false;
        },
        () => {
          return true;
        },
      ),
    );
  },
};
